// frontend/src/components/SafeZoneOverlay.tsx
import React from "react";

/**
 * Draws non-interactive safe-zone paddings over a video frame.
 * Defensive: works even if `zones` is missing or partially specified.
 */
export type VideoFormat = "9:16" | "16:9" | "1:1";

export interface Zone {
  top?: number; // px
  right?: number; // px
  bottom?: number; // px
  left?: number; // px
}

export interface SafeZoneOverlayProps {
  format?: VideoFormat;
  /**
   * Either a Zone object (px) or falsy to hide. If undefined, defaults per format are used.
   */
  zones?: Zone | false | null;
  className?: string;
}

const DEFAULTS: Record<VideoFormat, Required<Zone>> = {
  // conservative defaults; adjust in layout editor if needed
  "9:16": { top: 120, right: 40, bottom: 140, left: 40 },
  "16:9": { top: 60, right: 40, bottom: 60, left: 40 },
  "1:1": { top: 60, right: 40, bottom: 60, left: 40 },
};

function normalizeZones(format: VideoFormat, z?: Zone | false | null): Required<Zone> | null {
  if (z === false) return null; // explicitly disabled
  const base = DEFAULTS[format] || DEFAULTS["16:9"];
  const merged: Required<Zone> = {
    top: Math.max(0, Math.round((z?.top ?? base.top))),
    right: Math.max(0, Math.round((z?.right ?? base.right))),
    bottom: Math.max(0, Math.round((z?.bottom ?? base.bottom))),
    left: Math.max(0, Math.round((z?.left ?? base.left))),
  };
  // nothing to draw
  if (!merged.top && !merged.right && !merged.bottom && !merged.left) return null;
  return merged;
}

export default function SafeZoneOverlay({ format = "9:16", zones, className }: SafeZoneOverlayProps) {
  const z = normalizeZones(format, zones);
  if (!z) return null;

  return (
    <div
      className={["pointer-events-none absolute inset-0", className || ""].join(" ")}
      aria-hidden="true"
      data-safezone
    >
      {/* Why absolute blocks: reliable sizing regardless of container */}
      {z.top > 0 && (
        <div className="absolute left-0 right-0" style={{ top: 0, height: z.top, background: "rgba(59,130,246,0.15)" }} />
      )}
      {z.bottom > 0 && (
        <div className="absolute left-0 right-0" style={{ bottom: 0, height: z.bottom, background: "rgba(59,130,246,0.15)" }} />
      )}
      {z.left > 0 && (
        <div className="absolute top-0 bottom-0" style={{ left: 0, width: z.left, background: "rgba(59,130,246,0.12)" }} />
      )}
      {z.right > 0 && (
        <div className="absolute top-0 bottom-0" style={{ right: 0, width: z.right, background: "rgba(59,130,246,0.12)" }} />
      )}

      {/* center guide */}
      <div className="absolute inset-0 border border-white/20" style={{
        top: z.top,
        right: z.right,
        bottom: z.bottom,
        left: z.left,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.2)",
        borderRadius: 8,
      }} />
    </div>
  );
}
